/**
 * Review moment / Post Stream Review eligibility check.
 *
 * Returns true when the user is in the Review moment experiment **and** on the
 * xstate / orchestrator path. Review moment cannot run without xstate
 * (`buildCommitTransaction` bails if the streaming plugin is unregistered, and
 * the PSR UI shares modal anchoring with the legacy prompt).
 *
 * Optional Review moment knobs (step-through, show-diff-by-default, accepted
 * pill, diff-based segmenting) live on `platform_editor_review_moment_config`
 * and apply on top of this check.
 */
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

/**
 * Switcheroo key for the Review moment experiment.
 *
 * Exported so tests and callers reference a single source of truth for the
 * experiment name.
 */
export const REVIEW_MOMENT_EXPERIMENT = 'platform_editor_ai_review_moment';

/**
 * Returns true when Review moment is eligible: xstate migration AND the Review
 * moment experiment. See file-level JSDoc for why both are required.
 *
 * This variant **logs an experiment exposure** (Review moment last, so xstate
 * short-circuits first and only users who can receive the treatment are
 * enrolled). Use it only at canonical, action-driven gate-enforcement points —
 * not in render paths that run on every re-render.
 */
export function isReviewMomentEnabled(): boolean {
	return (
		expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true) &&
		isExperimentEnabled(REVIEW_MOMENT_EXPERIMENT)
	);
}

/**
 * Non-exposure-logging counterpart of `isReviewMomentEnabled`.
 *
 * Use this at render-time / read-only call sites where the canonical exposure
 * has already been logged at the gate-enforcement point.
 *
 * Defaults to `false`, so an absent or uninitialised experiment value leaves
 * Review moment off rather than exposing the UX unexpectedly.
 */
export function isReviewMomentEnabledNoExposure(): boolean {
	return (
		expValEqualsNoExposure('platform_editor_ai_xstate_migration', 'isEnabled', true) &&
		UNSAFE_expValNoExposure(REVIEW_MOMENT_EXPERIMENT, 'isEnabled', false)
	);
}
